/**
 * Card runner (Intake Builder). Runs a card and records the run as an EVENT
 * (Journal source). HONESTY: paste cards add a real node; Image → server route;
 * NCRP / device_extract parse client-side from schema; bank statement uses
 * StatementFlow when configured else client CSV parse; UFDR/E01 uses forensic
 * upload when a case is bound. Never fakes backend output.
 */
import type cytoscape from 'cytoscape';
import { useIntakeStore, newIntakeId, type IntakeCard, type RunEvent, type PendingNode } from '../../stores/useIntakeStore';
import { useFusionStore } from '../../stores/useFusionStore';
import { evidenceById, STATUS_META } from '../registry';
import { runLayout, fitTo, visibleEles, bloom } from '../../components/fusion/fusionLayout';
import { canonicalEntityId, linkDerivedIdentifiers, linkToSubject, normalizeIdentifier } from '../entityJoin';
import { persistCaseEdge } from '../../services/caseCanvas';
import { detectChainStrict } from '../../utils/chainDetect';
import { parseNcrpComplaint, type GraphBag } from './ncrpParser';
import { parseDeviceExtract, isDeviceExtractJsonFile, isForensicArchive } from './deviceExtract';
import { parseBankCsvClient } from './bankCsvParse';
import {
  statementFlow,
  isPdfPasswordRequiredError,
  pdfLooksPasswordProtected,
} from '../../services/statementFlow';
import { statementToElements } from '../../data/fusionBankAdapter';
import { forensicService, handleSessionExpired } from '../../services/api';
import { persistBankDossierEdges, saveBankDossierAsEvidence } from '../../data/promoteBankImport';
import { useBankImportStore, type BankImportDossier } from '../../stores/useBankImportStore';
import { isGuestDemo } from '../../utils/guestDemo';
import { caseWriteBlockedNow, VIEWER_ACTION_REASON } from '../caseAccess';

const _inFlight = new Set<string>();

const API = ((import.meta.env as unknown as Record<string, string>).VITE_API_URL) || 'http://localhost:8000/api/v1';
const cy = (): cytoscape.Core | undefined => {
  const c = (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
  return c && !c.destroyed() ? c : undefined;
};
const MODULE: Record<string, string> = { wallet: 'crypto', vpa: 'bank', phone: 'network', handle: 'social', document: 'shared', account: 'bank', person: 'shared', device: 'shared' };

function persistSubjectAndDerivedEdges(type: string, label: string) {
  const caseId = useIntakeStore.getState().caseId;
  if (!caseId) return;
  const mergeKey = normalizeIdentifier(type, label);
  if (!mergeKey) return;
  persistCaseEdge(caseId, {
    source: { module: 'shared', type: 'identity_hub', canonical_id: 'case', label: 'Subject (your data)' },
    target: { module: MODULE[type] || 'shared', type, canonical_id: mergeKey, label },
    edge_type: 'cross_link',
    determinism: 'scored',
    attribution: { source: 'fusion', method: 'subject_hub' },
  });
  if (type === 'vpa') {
    const local = mergeKey.split('@')[0] || '';
    if (/^\d{10}$/.test(local)) {
      persistCaseEdge(caseId, {
        source: { module: 'network', type: 'phone', canonical_id: local, label: local },
        target: { module: 'bank', type: 'vpa', canonical_id: mergeKey, label },
        edge_type: 'cross_link',
        determinism: 'scored',
        attribution: { source: 'fusion', method: 'derived_join' },
      });
    }
  }
}

function record(e: RunEvent) {
  const s = useIntakeStore.getState();
  s.addRun(e);
  // Run rail parity with Palette/radial jobs (fusion-build-journal demo howTo).
  useFusionStore.getState().addJob({
    id: e.id,
    operator: e.registryId,
    label: e.title,
    targetId: e.targetNodeId,
    status: 'done',
    message: e.summary,
    ts: e.ts,
  });
  if (s.caseId) {
    void import('./journalSpine').then(({ persistCaseEvent, writeEventsCache, readEventsCache, runEventToJournal }) => {
      void persistCaseEvent(
        s.caseId!,
        `card.run:${e.registryId}`,
        {
          title: e.title,
          summary: e.summary,
          tier: e.tier,
          registryId: e.registryId,
          targetNodeId: e.targetNodeId,
          nodeLabel: e.nodeLabel,
        },
        e.id,
      ).then((serverId) => {
        const cached = readEventsCache(s.caseId!) || [];
        const entry = runEventToJournal(e);
        if (serverId) entry.id = serverId;
        entry.pending = !serverId;
        writeEventsCache(
          s.caseId!,
          [entry, ...cached.filter((x) => x.id !== e.id && x.id !== serverId)].slice(0, 200),
        );
      });
    });
  }
}

function isEdgeBag(b: GraphBag | PendingNode): boolean {
  return !!(b as GraphBag).source && !!(b as GraphBag).target;
}

/** Apply node/edge bags to the live canvas, or queue them while Build has no canvas. */
function applyBags(bags: GraphBag[]): string[] {
  const nodeIds: string[] = [];
  const c = cy();
  const nodes = bags.filter((b) => !isEdgeBag(b));
  const edges = bags.filter((b) => isEdgeBag(b));

  if (c) {
    for (const data of nodes) {
      const id = String(data.id);
      if (c.getElementById(id).empty()) {
        try { c.add({ data }); } catch { /* skip */ }
      }
      if (id.startsWith('ent:') && data.type !== 'identity_hub') linkToSubject(c, id);
      nodeIds.push(id);
    }
    for (const data of edges) {
      const id = String(data.id);
      if (c.getElementById(id).empty()) {
        try { c.add({ data }); } catch { /* skip */ }
      }
    }
    linkDerivedIdentifiers(c);
    runLayout(c, visibleEles(c));
    fitTo(c, visibleEles(c), 55);
    bloom(c, nodeIds.slice(0, 12));
    if (nodeIds[0]) useFusionStore.getState().select({ id: nodeIds[0], kind: 'node' });
  } else {
    const pending = useIntakeStore.getState().pendingNodes;
    for (const data of [...nodes, ...edges]) {
      const id = String(data.id);
      if (!pending.some((p) => p.id === id)) {
        useIntakeStore.getState().enqueueNode(data as PendingNode);
      }
      if (!isEdgeBag(data)) nodeIds.push(id);
    }
  }
  return nodeIds;
}

function addNode(type: string, label: string, extras?: Record<string, unknown>): string {
  const id = canonicalEntityId(type, label);
  const data: GraphBag = {
    id, label, module: MODULE[type] || 'shared', type, risk_score: 0.3, size: 4, step: 1,
    ...extras,
  };
  applyBags([data]);
  return id;
}

/** What a failed card save says, in words an officer can act on. */
export function saveFailureReason(status: number | null): string {
  if (status === null) return 'the server could not be reached';
  if (status === 401) return 'your session has expired, sign in again';
  if (status === 403) return 'you do not have edit rights on this case';
  if (status === 404) return 'the case was not found';
  return `the server answered ${status}`;
}

interface SaveItem { type: string; label: string; chain?: string }

/** POST one node to the case graph. Resolves with the failure reason, or null when it was saved. */
async function postNode(caseId: string, item: SaveItem): Promise<string | null> {
  const token = localStorage.getItem('token') || '';
  const mergeKey = normalizeIdentifier(item.type, item.label);
  const attribution: Record<string, unknown> = { source: 'fusion_intake', method: 'card_persist' };
  if (item.type === 'wallet') {
    const chain = item.chain || detectChainStrict(mergeKey) || undefined;
    if (chain) attribution.chain = chain;
  } else if (item.type === 'tx' && item.chain) {
    // A transaction hash carries the chain the screen named (INTAKE03); the worker reads it on that chain.
    attribution.chain = item.chain;
  }
  let res: Response;
  try {
    res = await fetch(`${API}/canvas/${caseId}/nodes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        module: MODULE[item.type] || 'crypto',
        type: item.type,
        canonical_id: mergeKey,
        label: item.label,
        attribution,
      }),
    });
  } catch {
    return saveFailureReason(null);
  }
  if (res.ok) return null;
  if (res.status === 401) handleSessionExpired();
  return saveFailureReason(res.status);
}

/** Mark the card: the node is on the canvas but the case does not hold it. */
function markSaveFailed(cardId: string, failed: SaveItem[], reason: string) {
  const store = useIntakeStore.getState();
  const card = store.cards.find((c) => c.id === cardId);
  const known = card?.saveFailed?.items ?? [];
  const items = [...known, ...failed.filter((f) => !known.some((k) => k.type === f.type && k.label === f.label))];
  console.error('fusion.card_save_failed', { cardId, reason, items: items.length });
  store.patchCard(cardId, {
    status: 'error',
    message: `Shown on the canvas but not saved to the case: ${reason}.`,
    saveFailed: { reason, items },
  });
  store.addRun({
    id: newIntakeId('run'), ts: Date.now(), cardId, registryId: card?.registryId ?? '',
    title: 'Card save failed',
    summary: `${items.length} item(s) not saved to the case: ${reason}`,
    tier: null,
  });
}

/** Save a card's node to the case; a failure shows on the card with a retry. */
function persistNode(cardId: string, type: string, label: string, extras?: { chain?: string }) {
  const caseId = useIntakeStore.getState().caseId;
  if (!caseId) return;
  const item: SaveItem = { type, label, chain: extras?.chain };
  void postNode(caseId, item).then((reason) => {
    if (reason) markSaveFailed(cardId, [item], reason);
  });
}

/** Retry the saves that failed on a card; clears the failure when all are saved. */
export async function retryCardSave(card: IntakeCard): Promise<void> {
  const caseId = useIntakeStore.getState().caseId;
  const failed = card.saveFailed;
  if (!caseId || !failed) return;
  const store = useIntakeStore.getState();
  store.patchCard(card.id, { status: 'running', message: 'Saving to the case…' });
  const stillFailing: SaveItem[] = [];
  let lastReason = failed.reason;
  for (const item of failed.items) {
    const reason = await postNode(caseId, item);
    if (reason) { stillFailing.push(item); lastReason = reason; }
  }
  if (stillFailing.length) {
    store.patchCard(card.id, { saveFailed: undefined });
    markSaveFailed(card.id, stillFailing, lastReason);
    return;
  }
  store.patchCard(card.id, { status: 'done', message: 'Saved to the case.', saveFailed: undefined });
}

function fileFromInput(v: unknown): File | undefined {
  return v instanceof File ? v : undefined;
}

async function runBankStatement(card: IntakeCard, title: string) {
  const store = useIntakeStore.getState();
  const file = fileFromInput(card.inputs.file);
  if (!file) {
    store.patchCard(card.id, { status: 'error', message: 'Choose a bank statement file (PDF / XLSX / CSV)' });
    return;
  }
  const holder = String(card.inputs.holder || store.subjectName || 'Subject').trim() || 'Subject';
  const password = String(card.inputs.password || '').trim();

  // Live StatementFlow path when configured.
  if (statementFlow.isConfigured()) {
    const needsPw = await pdfLooksPasswordProtected(file);
    if (needsPw && !password) {
      store.patchCard(card.id, { status: 'error', message: 'PDF is password-protected — enter the statement password on the card' });
      return;
    }
    try {
      const cid = await statementFlow.ensureCase();
      const pid = await statementFlow.ensurePerson(cid, holder);
      const up = await statementFlow.uploadStatement(cid, pid, file, password ? { password } : undefined);
      const st = await statementFlow.waitForParse(up.statement.id, {
        seed: up.statement,
        transactionCount: up.transaction_count,
      });
      let kyc = up.account || null;
      if (!kyc?.ifsc && !kyc?.holder_name) {
        kyc = await statementFlow.getAccount(cid, st.account_id);
      }
      const txns = await statementFlow.getTransactions(cid, st.account_id);
      const accountLabel = kyc?.holder_name || st.source_file_name?.replace(/\.[^.]+$/, '') || holder;
      const res = statementToElements({ accountId: st.account_id, accountLabel, txns, kyc });
      const bags = res.elements.map((el) => el.data as GraphBag);
      applyBags(bags);
      // Write like the Bank import panel (FusionBankImport): the dossier goes to
      // Case files, the account and counterparty edges to the case spine, and the
      // statement summary to the Evidence Locker. Before W3-11 the card was on-screen only.
      const leCaseId = useIntakeStore.getState().caseId;
      const dossier: BankImportDossier = {
        leCaseId,
        sfCaseId: cid,
        statementId: st.id,
        accountId: st.account_id,
        accountNodeId: res.accountNodeId,
        fileName: file.name,
        statement: {
          source_file_name: st.source_file_name,
          narrative_summary: st.narrative_summary,
          risk_level: st.risk_level,
          anomalies: st.anomalies,
          extracted_txn_count: st.extracted_txn_count,
        },
        kyc,
        summary: { txns: txns.length, cps: res.counterpartyCount, dr: res.drTotal, cr: res.crTotal },
        transactions: res.transactions,
        counterparties: res.counterparties,
        deepLink: statementFlow.deepLink(cid, st.id),
        importedAt: new Date().toISOString(),
      };
      useBankImportStore.getState().setDossier(dossier);
      if (leCaseId && !isGuestDemo()) {
        persistBankDossierEdges(leCaseId, dossier);
        try {
          await saveBankDossierAsEvidence(leCaseId, dossier);
        } catch {
          /* non-fatal, as in the panel: canvas, spine edges and Case files still hold the import */
        }
      }
      if (kyc?.holder_name && !store.subjectName.trim()) {
        useIntakeStore.getState().setSubjectName(kyc.holder_name);
      }
      const msg = `StatementFlow · ${txns.length} txn(s) · ${res.counterpartyCount} counterparty(ies)`;
      store.patchCard(card.id, {
        status: 'done',
        producedType: 'account',
        producedNodeId: res.accountNodeId,
        message: msg,
      });
      record({
        id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E1', title,
        summary: msg, tier: 'deterministic', targetNodeId: res.accountNodeId, nodeLabel: accountLabel,
      });
      window.dispatchEvent(new CustomEvent('intake:goto-canvas'));
      return;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const pwFail = (e as { passwordRequired?: boolean })?.passwordRequired === true
        || isPdfPasswordRequiredError(message);
      if (pwFail) {
        store.patchCard(card.id, {
          status: 'error',
          message: password
            ? 'Incorrect PDF password — check DOB / account / phone digits and retry'
            : 'PDF password required — enter it on the card',
        });
        return;
      }
      // Fall through to CSV client parse when StatementFlow fails on a CSV.
      if (!/\.csv$/i.test(file.name)) {
        store.patchCard(card.id, { status: 'error', message: message || 'StatementFlow import failed' });
        record({ id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E1', title, summary: `import failed: ${message}`, tier: null });
        return;
      }
    }
  }

  // Client CSV parse (sandbox / StatementFlow off).
  if (/\.csv$/i.test(file.name) || file.type === 'text/csv') {
    try {
      const text = await file.text();
      const parsed = parseBankCsvClient(text, file.name);
      const bags = parsed.elements.elements.map((el) => el.data as GraphBag);
      applyBags(bags);
      if (holder && holder !== 'Subject' && !store.subjectName.trim()) {
        useIntakeStore.getState().setSubjectName(holder);
      }
      store.patchCard(card.id, {
        status: 'done',
        producedType: 'account',
        producedNodeId: parsed.elements.accountNodeId,
        message: parsed.summary,
      });
      record({
        id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E1', title,
        summary: parsed.summary, tier: 'deterministic',
        targetNodeId: parsed.elements.accountNodeId, nodeLabel: parsed.accountLabel,
      });
      window.dispatchEvent(new CustomEvent('intake:goto-canvas'));
      return;
    } catch (e) {
      store.patchCard(card.id, { status: 'error', message: e instanceof Error ? e.message : 'CSV parse failed' });
      return;
    }
  }

  // PDF/XLSX without StatementFlow — stage into the Import aside honestly.
  window.dispatchEvent(new CustomEvent('fusion:drop-file', { detail: file }));
  window.dispatchEvent(new CustomEvent('fusion:open-tab', { detail: 'import' }));
  store.patchCard(card.id, {
    status: 'error',
    message: 'StatementFlow not configured — file staged on Canvas → Import. Set VITE_STATEMENTFLOW_URL, or use a CSV.',
  });
  record({
    id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E1', title,
    summary: 'StatementFlow not configured — staged file on Import tab',
    tier: null,
  });
}

async function runDeviceCard(card: IntakeCard, title: string) {
  const store = useIntakeStore.getState();
  const file = fileFromInput(card.inputs.file);
  const paste = String(card.inputs.value || '').trim();

  // JSON path (file or paste) — device_extract.v1
  if (paste || (file && isDeviceExtractJsonFile(file))) {
    try {
      const raw = paste || (file ? await file.text() : '');
      const parsed = parseDeviceExtract(raw);
      applyBags(parsed.bags);
      for (const b of parsed.bags) {
        if (!isEdgeBag(b) && ['wallet', 'vpa', 'phone', 'handle'].includes(String(b.type))) {
          persistNode(card.id, String(b.type), String(b.label));
          persistSubjectAndDerivedEdges(String(b.type), String(b.label));
        }
      }
      store.patchCard(card.id, {
        status: 'done',
        producedType: parsed.producedType,
        producedNodeId: parsed.producedNodeId,
        message: parsed.summary,
      });
      record({
        id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E3', title,
        summary: parsed.summary, tier: 'deterministic',
        targetNodeId: parsed.producedNodeId || parsed.deviceNodeId,
        nodeLabel: parsed.summary,
      });
      window.dispatchEvent(new CustomEvent('intake:goto-canvas'));
      return;
    } catch (e) {
      store.patchCard(card.id, { status: 'error', message: e instanceof Error ? e.message : 'device_extract parse failed' });
      return;
    }
  }

  if (!file) {
    store.patchCard(card.id, {
      status: 'error',
      message: 'Upload a UFDR/E01, or paste / load device_extract.v1 JSON',
    });
    return;
  }

  // Forensic archive → backend when case bound.
  if (isForensicArchive(file) && store.caseId) {
    try {
      await forensicService.upload(store.caseId, file, undefined, 'Fusion Build · seized device');
      const overlay = await forensicService.getCanvasOverlay(store.caseId);
      const bags: GraphBag[] = [];
      for (const n of overlay.nodes || []) {
        const addr = String(n.address || '').trim();
        if (!addr) continue;
        const id = canonicalEntityId('wallet', addr);
        bags.push({
          id,
          label: addr,
          module: 'crypto',
          type: 'wallet',
          chain: n.chain,
          risk_score: n.kind === 'forensic_self' ? 0.55 : 0.4,
          size: 4,
          step: 1,
          placement_role: n.kind === 'forensic_self' ? 'subject' : 'context',
          forensic_kind: n.kind,
          attribution: { source: 'forensic_upload', method: 'canvas_overlay', confidence: 1 },
        });
      }
      for (const e of overlay.edges || []) {
        const srcAddr = e.source_address ? String(e.source_address) : '';
        const tgtAddr = String(e.target_address || '');
        if (!tgtAddr) continue;
        const source = srcAddr ? canonicalEntityId('wallet', srcAddr) : undefined;
        const target = canonicalEntityId('wallet', tgtAddr);
        if (!source) continue;
        bags.push({
          id: `forensic:edge:${e.record_id}:${e.tx_hash || e.exchange_internal_id || tgtAddr}`,
          label: e.direction || 'movement',
          module: 'crypto',
          type: 'edge',
          source,
          target,
          edge_type: 'trace',
          determinism: e.verified ? 'deterministic' : 'scored',
          step: 1,
          value: e.amount ? Number(e.amount) : undefined,
          unit: e.currency || undefined,
          _edgeLabel: e.direction || 'movement',
        });
      }
      // Always mint a device node anchored to the upload.
      const deviceId = `ent:device:${file.name.replace(/\W+/g, '').slice(0, 32) || 'seized'}`;
      bags.unshift({
        id: deviceId,
        label: file.name,
        module: 'shared',
        type: 'device',
        risk_score: 0.45,
        size: 7,
        step: 1,
        placement_role: 'source',
        attribution: { source: 'forensic_upload', method: 'ufdr_e01', confidence: 1 },
      });
      applyBags(bags);
      const nCount = (overlay.nodes || []).length;
      const msg = `Forensic upload · ${file.name} · ${nCount} overlay node(s)`;
      const first = bags.find((b) => b.type === 'wallet') || bags[0];
      store.patchCard(card.id, {
        status: 'done',
        producedType: first?.type === 'edge' ? 'device' : String(first?.type || 'device'),
        producedNodeId: String(first?.id || deviceId),
        message: msg,
      });
      record({
        id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E3', title,
        summary: msg, tier: 'deterministic', targetNodeId: String(first?.id || deviceId), nodeLabel: file.name,
      });
      window.dispatchEvent(new CustomEvent('intake:goto-canvas'));
      return;
    } catch (e) {
      store.patchCard(card.id, {
        status: 'error',
        message: e instanceof Error ? e.message : 'Forensic upload failed',
      });
      record({
        id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E3', title,
        summary: `forensic upload failed: ${e instanceof Error ? e.message : String(e)}`,
        tier: null,
      });
      return;
    }
  }

  if (isForensicArchive(file) && !store.caseId) {
    store.patchCard(card.id, {
      status: 'error',
      message: 'UFDR/E01 needs a bound case (open /fusion while logged in). Or use device_extract.v1 JSON for sandbox.',
    });
    return;
  }

  store.patchCard(card.id, {
    status: 'error',
    message: 'Unsupported file — use .ufdr/.e01/.zip on a case, or device_extract.v1 JSON',
  });
}

async function runNcrpCard(card: IntakeCard, title: string) {
  const store = useIntakeStore.getState();
  const file = fileFromInput(card.inputs.file);
  const paste = String(card.inputs.value || '').trim();
  let raw = paste;
  if (!raw && file) raw = await file.text();
  try {
    const parsed = parseNcrpComplaint(raw);
    applyBags(parsed.bags);
    if (parsed.victimName && !store.subjectName.trim()) {
      useIntakeStore.getState().setSubjectName(parsed.victimName);
    }
    for (const b of parsed.bags) {
      if (!isEdgeBag(b) && ['wallet', 'vpa', 'phone', 'handle', 'account', 'person'].includes(String(b.type))) {
        persistNode(card.id, String(b.type), String(b.label));
        if (['wallet', 'vpa', 'phone', 'handle', 'account'].includes(String(b.type))) {
          persistSubjectAndDerivedEdges(String(b.type), String(b.label));
        }
      }
    }
    store.patchCard(card.id, {
      status: 'done',
      producedType: parsed.producedType || 'document',
      producedNodeId: parsed.producedNodeId,
      message: parsed.summary,
    });
    record({
      id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E8', title,
      summary: parsed.summary, tier: 'deterministic',
      targetNodeId: parsed.producedNodeId, nodeLabel: parsed.victimName || parsed.complaintId,
    });
    railDone(card.registryId, title, parsed.summary, parsed.producedNodeId);
    window.dispatchEvent(new CustomEvent('intake:goto-canvas'));
  } catch (e) {
    store.patchCard(card.id, { status: 'error', message: e instanceof Error ? e.message : 'NCRP parse failed' });
  }
}

/** Exchange subpoena / forensic PDF — same overlay path as UFDR when a case is bound. */
async function runExchangeSubpoenaCard(card: IntakeCard, title: string) {
  const store = useIntakeStore.getState();
  const file = fileFromInput(card.inputs.file);
  if (!file) {
    store.patchCard(card.id, { status: 'error', message: 'Choose an exchange / forensic file' });
    return;
  }
  if (!store.caseId) {
    store.patchCard(card.id, {
      status: 'error',
      message: 'Needs a bound case (stay logged in on /fusion). Sandbox bind runs on page load.',
    });
    return;
  }
  try {
    await forensicService.upload(store.caseId, file, undefined, 'Fusion Build · exchange subpoena');
    const overlay = await forensicService.getCanvasOverlay(store.caseId);
    const bags: GraphBag[] = [];
    for (const n of overlay.nodes || []) {
      const addr = String(n.address || '').trim();
      if (!addr) continue;
      const id = canonicalEntityId('wallet', addr);
      bags.push({
        id,
        label: addr,
        module: 'crypto',
        type: 'wallet',
        chain: n.chain,
        risk_score: n.kind === 'forensic_self' ? 0.55 : 0.4,
        size: 4,
        step: 1,
        placement_role: n.kind === 'forensic_self' ? 'subject' : 'context',
        forensic_kind: n.kind,
        attribution: { source: 'forensic_upload', method: 'exchange_subpoena', confidence: 1 },
      });
    }
    applyBags(bags);
    const nCount = (overlay.nodes || []).length;
    const msg = `Forensic upload · ${file.name} · ${nCount} overlay node(s)`;
    const first = bags[0];
    store.patchCard(card.id, {
      status: 'done',
      producedType: 'wallet',
      producedNodeId: first ? String(first.id) : undefined,
      message: msg,
    });
    record({
      id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E2', title,
      summary: msg, tier: 'deterministic', targetNodeId: first ? String(first.id) : undefined, nodeLabel: file.name,
    });
    railDone(card.registryId, title, msg, first ? String(first.id) : undefined);
    window.dispatchEvent(new CustomEvent('intake:goto-canvas'));
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Exchange subpoena upload failed';
    store.patchCard(card.id, { status: 'error', message });
    record({
      id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E2', title,
      summary: `upload failed: ${message}`, tier: null,
    });
  }
}

/** Client document node until locker seal is wired end-to-end on Fusion. */
async function runDocumentCard(card: IntakeCard, title: string) {
  const store = useIntakeStore.getState();
  const file = fileFromInput(card.inputs.file);
  if (!file) {
    store.patchCard(card.id, { status: 'error', message: 'Choose a document file' });
    return;
  }
  const note = String(card.inputs.note || '').trim();
  const nodeId = addNode('document', file.name, {
    file_name: file.name,
    file_size: file.size,
    note: note || undefined,
    sealed: false,
    attribution: { source: 'fusion_build', method: 'document_attach', confidence: 0.6 },
  });
  const msg = `Document node · ${file.name} (locker seal WIP)`;
  store.patchCard(card.id, {
    status: 'done',
    producedType: 'document',
    producedNodeId: nodeId,
    message: msg,
  });
  record({
    id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E10', title,
    summary: msg, tier: null, targetNodeId: nodeId, nodeLabel: file.name,
  });
  railDone(card.registryId, title, msg, nodeId);
  window.dispatchEvent(new CustomEvent('intake:goto-canvas'));
}

function railDone(operator: string, label: string, message: string, targetId?: string) {
  useFusionStore.getState().addJob({
    id: newIntakeId('job'),
    operator,
    label,
    targetId,
    status: 'done',
    message,
    ts: Date.now(),
  });
}

async function runCaseNoteCard(card: IntakeCard, title: string) {
  const store = useIntakeStore.getState();
  const noteTitle = String(card.inputs.title || '').trim();
  const body = String(card.inputs.body || '').trim();
  if (!noteTitle) {
    store.patchCard(card.id, { status: 'error', message: 'Enter a title' });
    return;
  }
  if (!body) {
    store.patchCard(card.id, { status: 'error', message: 'Enter note text' });
    return;
  }
  // Unique merge key so two notes with the same title stay distinct.
  const mergeLabel = `${noteTitle} · ${body.slice(0, 48)}`;
  const nodeId = addNode('document', mergeLabel, {
    kind: 'case_note',
    note: body,
    title: noteTitle,
    label: noteTitle,
  });
  persistNode(card.id, 'document', mergeLabel);
  persistSubjectAndDerivedEdges('document', mergeLabel);
  store.patchCard(card.id, {
    status: 'done',
    producedType: 'document',
    producedNodeId: nodeId,
    message: `Added case note: ${noteTitle}`,
  });
  record({
    id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E12', title,
    summary: `Case note: ${noteTitle}`,
    tier: 'deterministic', targetNodeId: nodeId, nodeLabel: noteTitle,
  });
  railDone(card.registryId, title, `Case note: ${noteTitle}`, nodeId);
  window.dispatchEvent(new CustomEvent('intake:goto-canvas'));
}

/**
 * The note appended to an image intake result when the admin-enabled cloud vision engine was called
 * (owner decision OD27): the image left this server, and how many candidates the checks kept.
 * Empty for the default on-box path.
 */
export function imageCloudNote(data: {
  cloud_engine_contributed?: boolean;
  external_llm_used?: boolean;
  cloud_vision?: { provider?: string; model?: string | null; accepted?: number; rejected?: number } | null;
}): string {
  if (data.cloud_engine_contributed === true) {
    const cv = data.cloud_vision || {};
    const who = `${String(cv.provider || 'provider')}${cv.model ? `/${String(cv.model)}` : ''}`;
    return ` · cloud vision (${who}) also read this image: ${Number(cv.accepted ?? 0)} accepted, ${Number(cv.rejected ?? 0)} rejected by the checks`;
  }
  if (data.external_llm_used === true) return ' · image was sent to the cloud vision provider but returned nothing usable';
  return '';
}

export async function runCard(card: IntakeCard) {
  if (_inFlight.has(card.id)) return;
  if (caseWriteBlockedNow()) {
    useIntakeStore.getState().patchCard(card.id, { status: 'error', message: VIEWER_ACTION_REASON });
    return;
  }
  _inFlight.add(card.id);
  const store = useIntakeStore.getState();
  const ev = evidenceById(card.registryId);
  const title = ev?.name || card.registryId;
  store.patchCard(card.id, { status: 'running', message: undefined });

  try {
    if (card.registryId === 'E8') {
      await runNcrpCard(card, title);
      return;
    }
    if (card.registryId === 'E1') {
      await runBankStatement(card, title);
      return;
    }
    if (card.registryId === 'E3') {
      await runDeviceCard(card, title);
      return;
    }
    if (card.registryId === 'E2') {
      await runExchangeSubpoenaCard(card, title);
      return;
    }
    if (card.registryId === 'E10') {
      await runDocumentCard(card, title);
      return;
    }
    if (card.registryId === 'E12') {
      await runCaseNoteCard(card, title);
      return;
    }

    // Paste cards (Wallet/VPA/Phone/Handle) → a real typed node.
    if (ev?.produces && card.inputs.value) {
      const label = String(card.inputs.value).trim();
      if (!label) { store.patchCard(card.id, { status: 'error', message: 'Enter a value' }); return; }
      const extras: Record<string, unknown> = {};
      if (ev.produces === 'wallet') {
        const chain = detectChainStrict(label);
        if (chain) extras.chain = chain;
      }
      if (card.registryId === 'E9') extras.platform = 'instagram';
      if (card.registryId === 'E7') extras.platform = 'telegram';
      const nodeId = addNode(ev.produces, label, extras);
      persistNode(card.id, ev.produces, label);
      persistSubjectAndDerivedEdges(ev.produces, label);
      const chainNote = extras.chain ? ` (${extras.chain})` : '';
      store.patchCard(card.id, {
        status: 'done',
        producedType: ev.produces,
        producedNodeId: nodeId,
        message: `Added ${ev.produces} node${chainNote}`,
      });
      record({
        id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: card.registryId, title,
        summary: `Added ${ev.produces}: ${label}${chainNote}`,
        tier: 'deterministic', targetNodeId: nodeId, nodeLabel: label,
      });
      railDone(card.registryId, title, `Added ${ev.produces}: ${label}${chainNote}`, nodeId);
      window.dispatchEvent(new CustomEvent('intake:goto-canvas'));
      return;
    }

    // Image → server route (SHA-256 → locker → OCR/QR).
    if (card.registryId === 'E11') {
      const file = card.inputs.file as File | undefined;
      if (!file) { store.patchCard(card.id, { status: 'error', message: 'Choose an image first' }); return; }
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('ocr', String(card.inputs.ocr !== false));
        fd.append('qr', String(card.inputs.qr !== false));
        fd.append('vision', String(!!card.inputs.vision));
        fd.append('cloud_vision', String(card.inputs.cloud_vision !== false));
        if (store.caseId) fd.append('case_id', store.caseId);
        if (card.inputs.note) fd.append('note', String(card.inputs.note));
        const token = localStorage.getItem('token') || '';
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 60000);
        let res: Response;
        try {
          res = await fetch(`${API}/intake/image`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd, signal: ctrl.signal });
        } finally {
          clearTimeout(timer);
        }
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const ids: string[] = [];
        for (const a of (data.extracted || [])) {
          const type = String(a.type || 'document'); const value = String(a.value);
          const id = addNode(type, value); if (id) ids.push(id);
          persistNode(card.id, type, value, a.chain ? { chain: String(a.chain) } : undefined);
          persistSubjectAndDerivedEdges(type, value);
        }
        const sealed = data.sealed === true;
        const artefacts = `${ids.length} artefact(s)`;
        // When the admin-enabled cloud engine was called, say so: the image left this server.
        const cloudNote = imageCloudNote(data);
        const doneMsg = (sealed ? `Sealed to locker · ${artefacts}` : `Preview — not sealed (no case) · ${artefacts}`) + cloudNote;
        const shaShort = String(data.sha256 || '').slice(0, 10);
        store.patchCard(card.id, { status: 'done', message: doneMsg });
        record({ id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E11', title, summary: `${sealed ? 'Image sealed' : 'Image preview (not sealed)'} (sha ${shaShort}…) · ${artefacts}${cloudNote}`, tier: sealed ? 'deterministic' : null });
      } catch (imgErr) {
        const aborted = imgErr instanceof Error && imgErr.name === 'AbortError';
        store.patchCard(card.id, { status: 'error', message: aborted ? 'Image ingest timed out — retry.' : 'Image ingest failed — server route unreachable or OCR unavailable. Retry.' });
        record({ id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: 'E11', title, summary: aborted ? 'image ingest timed out (client abort after 60s)' : 'image ingest failed (route/OCR) — image is server-hashed to the locker; no client-side OCR by policy', tier: null });
      }
      return;
    }

    // Everything else → honest binding report.
    const meta = ev ? STATUS_META[ev.status] : null;
    const summary = ev ? `${meta?.label} · ${ev.binding} — runs against an ingested case` : 'wired';
    store.patchCard(card.id, { status: 'done', message: ev ? `${meta?.label} · ${ev.binding}` : 'wired' });
    record({ id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: card.registryId, title, summary, tier: null });
  } catch (err) {
    store.patchCard(card.id, { status: 'error', message: 'Run failed — the canvas may not be ready. Open the Canvas tab and try again.' });
    record({ id: newIntakeId('run'), ts: Date.now(), cardId: card.id, registryId: card.registryId, title, summary: `run failed: ${err instanceof Error ? err.message : String(err)}`, tier: null });
  } finally {
    _inFlight.delete(card.id);
  }
}
